"use client";

import { ld } from "@/lib/theme/light-dark";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { type CSSProperties, type ReactNode, useMemo, useState } from "react";

import { useTranslations } from "next-intl";
import {
  type HeroEmailSplit,
  splitHeroEmailBody,
  splitHeroPortalBody,
} from "./hero-vorgang-email-split";

/** Same stack as the app's email iframe — mail text should not look like app UI. */
const EMAIL_FONT: string =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const EMAIL_LINK_COLOR: string = "#006b5e";
// i18n-exempt: domain name in the mail signature
const SIGNATURE_LINK: string = "www.hausverwalter.ai";

/** White mail canvas — stays white in dark mode, like the app's email frame. */
const EMAIL_CANVAS: CSSProperties = {
  margin: "10px 16px 0",
  padding: 12,
  borderRadius: 6,
  background: "#fff",
  color: "#18181b",
  fontFamily: EMAIL_FONT,
  fontSize: 13,
  lineHeight: 1.5,
  wordBreak: "break-word",
};

const QUOTE_STYLE: CSSProperties = {
  margin: "6px 0 0",
  paddingLeft: 10,
  borderLeft: "2px solid #d4d4d8",
  color: "#52525b",
};

type QuoteBlock = { readonly quoted: boolean; readonly lines: string[] };

/** `>`-quoted plain text as nested blockquotes, like a mail client renders it. */
function QuotedPlain(props: { readonly text: string }): ReactNode {
  const blocks: QuoteBlock[] = [];
  for (const line of props.text.split("\n")) {
    const quoted: boolean = line.startsWith(">");
    const content: string = quoted ? line.replace(/^> ?/, "") : line;
    const last: QuoteBlock | undefined = blocks[blocks.length - 1];
    if (last !== undefined && last.quoted === quoted) last.lines.push(content);
    else blocks.push({ quoted, lines: [content] });
  }
  return blocks.map((block: QuoteBlock, index: number) => {
    const text: string = block.lines.join("\n").trim();
    if (text.length === 0) return null;
    return block.quoted ? (
      <blockquote key={index} style={QUOTE_STYLE}>
        <QuotedPlain text={text} />
      </blockquote>
    ) : (
      <div key={index} style={{ whiteSpace: "pre-wrap", marginTop: index > 0 ? 6 : 0 }}>
        {text}
      </div>
    );
  });
}

/** HTML signature with logo image, as sent from the app. */
function EmailSignature(): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        marginTop: 12,
        paddingTop: 10,
        borderTop: "1px solid #e4e4e7",
      }}
    >
      <BrandLogoMark width={26} height={26} variant="dark" />
      <span style={{ color: EMAIL_LINK_COLOR, fontWeight: 500, textDecoration: "underline" }}>
        {SIGNATURE_LINK}
      </span>
    </div>
  );
}

export function HeroVorgangMessageBody(props: {
  readonly channel: "email" | "portal";
  readonly direction: "inbound" | "outbound";
  readonly fullPlain: string;
  readonly signatureName?: string;
}): ReactNode {
  const t = useTranslations("marketing.heroVorgangMessageBody");
  const [showFullHistory, setShowFullHistory] = useState<boolean>(false);

  const split: HeroEmailSplit = useMemo((): HeroEmailSplit => {
    if (props.channel === "portal") {
      return splitHeroPortalBody(props.fullPlain);
    }
    return splitHeroEmailBody(props.fullPlain, props.direction);
  }, [props.channel, props.direction, props.fullPlain]);

  if (props.channel === "portal") {
    return (
      <p
        style={{
          margin: 0,
          padding: "8px 16px 14px",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontSize: 13,
          lineHeight: 1.62,
          color: ld("#2A2F3A", "#e0e4e9"),
        }}
      >
        {split.visible}
      </p>
    );
  }

  const showSignature: boolean =
    props.direction === "outbound" &&
    props.signatureName !== undefined &&
    props.signatureName.length > 0;

  return (
    <div style={{ paddingBottom: split.canToggle ? 0 : 14 }}>
      <div style={EMAIL_CANVAS}>
        <div style={{ whiteSpace: "pre-wrap" }}>{split.visible}</div>
        {showSignature ? <EmailSignature /> : null}
        {showFullHistory && split.hidden !== null ? (
          <div style={{ marginTop: 14 }}>
            <QuotedPlain text={split.hidden} />
          </div>
        ) : null}
      </div>

      {split.canToggle ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            className="mkhero-vorgang-verlauf-btn"
            aria-expanded={showFullHistory}
            onClick={() => {
              setShowFullHistory((prev: boolean) => !prev);
            }}
            style={{
              padding: "8px 0 10px",
              border: "none",
              background: "transparent",
              fontSize: 11,
              fontWeight: 500,
              lineHeight: 1,
              color: ld("rgba(15,23,42,0.45)", "rgba(250,250,250,0.45)"),
              cursor: "pointer",
            }}
          >
            {showFullHistory ? t("verlaufAusblenden") : t("verlaufAnzeigen")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
